// src/pages/AdminHandlersPage.tsx
//
// Admin page pra listar handlers system-wide + drilldown executions.
// PR9 (Fase 1.B). MVP: read-only via lookup direto. CRUD futuro.
// Reescrita usando o design system RAIOS (PageHeader, Card, Badge, StatusBadge, Sheet, Switch, Skeleton, EmptyState).

import { useEffect, useMemo, useState } from 'react';
import { api } from '@/lib/api';
import type { Handler, HandlerExecution } from '@/lib/api';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card } from '@/components/ui/Card';
import { Badge, StatusBadge, type StatusType } from '@/components/ui/Badge';
import { Skeleton } from '@/components/ui/Skeleton';
import { EmptyState } from '@/components/ui/EmptyState';
import { Switch } from '@/components/ui/Switch';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/Sheet';
import { ScrollArea } from '@/components/ui/ScrollArea';
import { Workflow, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';

/* ── Helpers ───────────────────────────────────────────────────────────────── */

const KIND_BADGE: Record<Handler['kind'], 'default' | 'info' | 'brand' | 'warning'> = {
  'task-bound': 'info',
  'demand-bound': 'brand',
  'project-bound': 'warning',
  'vision-bound': 'default',
};

const TYPE_BADGE: Record<Handler['handler_type'], 'default' | 'info' | 'brand' | 'success' | 'warning'> = {
  internal_op: 'default',
  skill: 'brand',
  agent: 'info',
  pipeline: 'success',
  script: 'warning',
  wizard: 'info',
};

const EXEC_STATUS_MAP: Record<HandlerExecution['status'], StatusType> = {
  pending: 'pending',
  running: 'running',
  completed: 'success',
  failed: 'error',
  cancelled: 'cancelled',
};

/* ── Skeletons ─────────────────────────────────────────────────────────────── */

function HandlersListSkeleton() {
  return (
    <div className="space-y-3">
      {[0, 1, 2, 3, 4].map((i) => (
        <Card key={i} className="p-4 space-y-2">
          <Skeleton className="h-4 w-1/3" />
          <Skeleton className="h-3 w-1/2" />
          <Skeleton className="h-3 w-2/3" />
        </Card>
      ))}
    </div>
  );
}

function ExecutionsSkeleton() {
  return (
    <div className="space-y-2">
      {[0, 1, 2].map((i) => (
        <div key={i} className="rounded-md border border-raios-border bg-raios-card p-3 space-y-2">
          <Skeleton className="h-4 w-1/2" />
          <Skeleton className="h-3 w-2/3" />
        </div>
      ))}
    </div>
  );
}

/* ── Page ──────────────────────────────────────────────────────────────────── */

export default function AdminHandlersPage() {
  const [handlers, setHandlers] = useState<Handler[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showInactive, setShowInactive] = useState(false);

  const [selected, setSelected] = useState<Handler | null>(null);
  const [executions, setExecutions] = useState<HandlerExecution[]>([]);
  const [execLoading, setExecLoading] = useState(false);
  const [execError, setExecError] = useState<string | null>(null);

  useEffect(() => {
    api
      .listHandlers()
      .then(setHandlers)
      .catch((err: Error) => setError(err.message ?? 'Falha ao carregar handlers'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!selected) {
      let cancelled = false;
      Promise.resolve().then(() => {
        if (!cancelled) setExecutions([]);
      });
      return () => {
        cancelled = true;
      };
    }
    let cancelled = false;
    Promise.resolve().then(() => {
      if (cancelled) return;
      setExecLoading(true);
      setExecError(null);
    });
    api
      .listHandlerExecutions({ handler_id: selected.id, limit: 50 })
      .then(setExecutions)
      .catch((err: Error) => {
        setExecError(err.message ?? 'Falha ao carregar execuções');
        setExecutions([]);
      })
      .finally(() => setExecLoading(false));
    return () => {
      cancelled = true;
    };
  }, [selected]);

  const filtered = useMemo(
    () => (showInactive ? handlers : handlers.filter((h) => h.is_active)),
    [handlers, showInactive],
  );

  const inactiveCount = useMemo(() => handlers.filter((h) => !h.is_active).length, [handlers]);

  return (
    <div className="p-4 md:p-6 space-y-6">
      <PageHeader
        title="Handlers"
        description="Handlers system-wide registrados (Fase 1.B). Read-only no MVP — clique em um handler pra inspecionar últimas execuções."
        actions={
          <div className="flex items-center gap-2">
            <Switch
              id="show-inactive"
              checked={showInactive}
              onCheckedChange={setShowInactive}
            />
            <label htmlFor="show-inactive" className="text-sm text-raios-text-secondary cursor-pointer">
              Mostrar inativos {inactiveCount > 0 && <span className="text-raios-muted">({inactiveCount})</span>}
            </label>
          </div>
        }
      />

      {error && (
        <Card className="bg-raios-danger/10 px-4 py-3">
          <p className="text-raios-danger-text text-sm">{error}</p>
        </Card>
      )}

      {loading ? (
        <HandlersListSkeleton />
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={Workflow}
          title={handlers.length === 0 ? 'Nenhum handler registrado' : 'Nenhum handler ativo'}
          description={
            handlers.length === 0
              ? 'Aguardando seed de handlers (Fase 1.B).'
              : 'Ative o toggle acima pra ver handlers inativos.'
          }
        />
      ) : (
        <div className="space-y-3">
          {filtered.map((h) => (
            <Card
              key={h.id}
              hoverable
              className="p-4 cursor-pointer"
              onClick={() => setSelected(h)}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                    <h3 className="text-sm font-mono font-semibold text-raios-text">{h.name}</h3>
                    <Badge variant={KIND_BADGE[h.kind]}>{h.kind}</Badge>
                    <Badge variant={TYPE_BADGE[h.handler_type]}>{h.handler_type}</Badge>
                    {!h.is_active && <Badge variant="warning">inativo</Badge>}
                  </div>
                  {h.description && (
                    <p className="text-sm text-raios-text-secondary line-clamp-2">{h.description}</p>
                  )}
                  {h.handler_module && (
                    <p className="text-[11px] font-mono text-raios-muted mt-1">{h.handler_module}</p>
                  )}
                </div>
                <ChevronRight className="w-4 h-4 text-raios-muted shrink-0 mt-1" />
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Drilldown: execuções recentes */}
      <Sheet open={!!selected} onOpenChange={(open) => !open && setSelected(null)}>
        <SheetContent className="w-[90vw] sm:w-[520px] flex flex-col">
          {selected && (
            <>
              <SheetHeader className="shrink-0">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <Badge variant={KIND_BADGE[selected.kind]}>{selected.kind}</Badge>
                  <Badge variant={TYPE_BADGE[selected.handler_type]}>{selected.handler_type}</Badge>
                  {!selected.is_active && <Badge variant="warning">inativo</Badge>}
                </div>
                <SheetTitle className="font-mono">{selected.name}</SheetTitle>
                <SheetDescription>
                  {selected.description ?? 'Sem descrição.'}
                </SheetDescription>
              </SheetHeader>

              <ScrollArea className="flex-1 px-6 py-4">
                <div className="space-y-4">
                  <div>
                    <h4 className="text-xs uppercase tracking-wider font-semibold text-raios-muted mb-2">
                      Últimas execuções (até 50)
                    </h4>

                    {execLoading ? (
                      <ExecutionsSkeleton />
                    ) : execError ? (
                      <p className="text-sm text-raios-danger-text">{execError}</p>
                    ) : executions.length === 0 ? (
                      <p className="text-sm text-raios-muted py-4">Sem execuções registradas.</p>
                    ) : (
                      <div className="space-y-2">
                        {executions.map((e) => (
                          <div
                            key={e.id}
                            className="rounded-md border border-raios-border bg-raios-card p-3"
                          >
                            <div className="flex items-center justify-between gap-2">
                              <span className="text-xs font-mono text-raios-text truncate">
                                {e.target_slug ?? '(sem slug)'}
                              </span>
                              <StatusBadge
                                status={EXEC_STATUS_MAP[e.status]}
                                label={e.status}
                              />
                            </div>
                            <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-raios-muted">
                              <span>origin: <span className="text-raios-text-secondary">{e.origin}</span></span>
                              <span>duração: <span className="text-raios-text-secondary">{e.duration_ms != null ? `${e.duration_ms}ms` : '—'}</span></span>
                              <span className={cn('font-mono')}>
                                {new Date(e.started_at).toLocaleString('pt-BR')}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </ScrollArea>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
